import {getTestProps} from '@benchling/js-test-utils';
import {Loader, Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState, KeyboardEvent, ChangeEvent, useRef, useCallback, useMemo, useEffect} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {AgentType, ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as ChatActions from 'llm/chat-library/ChatActions';
import {
  ChatDraftSettings,
  ChatDraftStatus,
  getDraftState,
  removeFileFromDraft,
  useChatDraftState,
} from 'llm/chat-library/ChatDraftState';
import {ChatSelectedFile} from 'llm/chat-library/ChatLibraryTypes';
import {
  getAgentTypeIcon,
  getAgentTypeText,
  isAgentTypeEnabled,
  getAgentTypePlaceholderText,
  doesAgentTypeSupportPublicData,
  getChatTypesForNewAgentChat,
  getAvailableAgentTypes,
  getChatTypeForNewAgentChat,
} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import WorkspaceContextChip from 'llm/chat-library/WorkspaceContextChip';
import useRealtimeVoiceSession, {
  RealtimeVoiceSessionControls,
  RealtimeVoiceSessionState,
} from 'llm/realtime-voice/useRealtimeVoiceSession';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY, GRAY_DARK, GRAY_DARKER, GRAY_LIGHTER, WHITE} from 'ui-library/Colors';
import FileList from 'ui-library/FileList';
import FileRow from 'ui-library/FileRow';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Spinner from 'ui-library/Spinner';
import DropTarget from 'util/drag-and-drop/DropTarget';
import isLikelyMobileCapabilities from 'util/isLikelyMobileCapabilities';

export interface ChatInputProps {
  chatSessionId: string | null;
  inputText: string;
  setInputText: (message: string) => void;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (enabled: boolean) => void;
  onSubmit: () => void;
  inputDisabled: boolean;
  /** All selected files (uploading, failed, completed) */
  selectedFiles: Array<ChatSelectedFile>;
  /** Called when user selects files to upload */
  onSelectFiles: (files: Array<File>) => void;
  /** Called when user removes a file (works for any status) */
  onRemoveFile: (key: string) => void;
  /**
   * The current agent type.
   * - Can be null while settings are still loading.
   * - When null, submission is blocked but the input is still usable.
   */
  agentType: AgentType | null;
  setAgentType: (agentType: AgentType | null) => void;
  allowedChatTypes: Array<ChatType>;
  hideAIWarning?: boolean;
}

/**
 * Chat input for new chats. Uses draft state initialized by openNewChat.
 */
export function NewChatInput({
  onCreateChat,
}: {
  onCreateChat: (chat: {id: string; type: ChatType}) => void;
}): JSX.Element {
  // Draft state is initialized by openNewChat with agentType and publicDataEnabled
  const controls = useChatInputControls(null);
  const {inputText, agentType, hasUploadingFiles} = controls;

  const {isSubmitting, sendMessage} = useNewChat({onCreateChat});

  const handleSubmitMessage = useCallback(async () => {
    if (!inputText.trim() || isSubmitting || !agentType || hasUploadingFiles) {
      return;
    }
    // Draft state already has everything - just submit
    await sendMessage();
  }, [agentType, inputText, isSubmitting, hasUploadingFiles, sendMessage]);

  return (
    <ChatInput
      chatSessionId={null}
      {...controls}
      onSubmit={handleSubmitMessage}
      inputDisabled={isSubmitting}
      allowedChatTypes={getChatTypesForNewAgentChat()}
      hideAIWarning={true}
    />
  );
}

export interface ChatInputControls {
  inputText: string;
  setInputText: (message: string) => void;
  /** All selected files (uploading, failed, completed) */
  selectedFiles: Array<ChatSelectedFile>;
  /** Whether any files are still uploading */
  hasUploadingFiles: boolean;
  /** Whether the draft message is pending (status is PENDING) */
  isPending: boolean;
  /** Called when user selects files to upload */
  onSelectFiles: (files: Array<File>) => void;
  /** Called when user removes a file (works for any status) */
  onRemoveFile: (key: string) => void;
  /**
   * The current agent type.
   * - Can be null while settings are still loading.
   * - For new chats, this comes from draft state initialized by openNewChat.
   * - For existing chats, this comes from draft state seeded by useMessageThread.
   */
  agentType: AgentType | null;
  setAgentType: (agentType: AgentType | null) => void;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (publicDataEnabled: boolean) => void;
}

// TODO: Unify with useMessageThread.
function useNewChat({onCreateChat}: {onCreateChat: (chat: {id: string; type: ChatType}) => void}): {
  isSubmitting: boolean;
  sendMessage: () => Promise<void>;
} {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const sendMessage = useCallback(async () => {
    // Read from draft state - it should already have everything from user input
    const draft = getDraftState(null);
    if (!draft) {
      return;
    }

    const agentType = draft.settings?.agentType;
    if (!agentType) {
      // This shouldn't happen - caller should check agentType before calling sendMessage
      return;
    }

    const chatType = getChatTypeForNewAgentChat(agentType);

    setIsSubmitting(true);
    try {
      const {chatSessionId} = await ChatActions.createChatWithDraftMessage(chatType);
      onCreateChat({id: chatSessionId, type: chatType});
    } finally {
      setIsSubmitting(false);
    }
  }, [onCreateChat]);

  return {isSubmitting, sendMessage};
}

/**
 * Custom hook for managing related input state.
 *
 * All draft state (text, files, settings) is persisted in global state via ChatDraftState.
 * This includes files being uploaded - they persist across component unmount/remount.
 *
 * For new chats: Draft state is initialized by openNewChat with agentType and publicDataEnabled.
 * For existing chats: Draft state is seeded by useMessageThread from the loaded thread.
 *
 * @param chatId - The chat session ID, or null for new chats.
 */
export function useChatInputControls(chatId: string | null): ChatInputControls {
  // Draft state is the single source of truth for everything.
  // useChatDraftState always returns a draft state object (never null).
  // For new chats, it includes the default agent type so the input is immediately usable.
  const [draftState, setDraft] = useChatDraftState(chatId);

  // Derive values from draft state.
  const agentType = draftState.settings?.agentType ?? null;
  const publicDataEnabled = draftState.settings?.publicDataEnabled ?? false;
  // When status is PENDING, the message is being sent - show empty input
  const inputText = draftState.status === ChatDraftStatus.PENDING ? '' : draftState.message.text;
  const selectedFiles = useMemo(() => draftState.message.selectedFiles, [draftState.message.selectedFiles]);
  const hasUploadingFiles = selectedFiles.some((f) => f.completedUpload == null);

  // Helper to update the draft state.
  // This only updates fields that are explicitly provided - it does NOT fill in defaults.
  const updateDraft = useCallback(
    (updates: Partial<ChatDraftSettings & {text: string}>) => {
      // Don't overwrite PENDING status - sendMessage is handling this
      if (draftState.status === ChatDraftStatus.PENDING) {
        return;
      }

      // Determine the new settings.
      // - If settings was already defined, preserve it (with updated values)
      // - If settings was undefined and we're setting an agentType, create settings
      // - If settings was undefined and no agentType, keep undefined (still loading)
      const newAgentType = updates.agentType ?? draftState.settings?.agentType;
      const newPublicDataEnabled =
        updates.publicDataEnabled ?? draftState.settings?.publicDataEnabled ?? false;
      const newSettings =
        newAgentType != null
          ? {
              agentType: newAgentType,
              publicDataEnabled: newPublicDataEnabled,
            }
          : undefined;

      setDraft({
        status: ChatDraftStatus.DRAFT,
        message: {
          text: updates.text ?? draftState.message.text,
          selectedFiles: draftState.message.selectedFiles,
        },
        settings: newSettings,
      });
    },
    [draftState, setDraft]
  );

  const setInputText = useCallback(
    (text: string) => {
      updateDraft({text});
    },
    [updateDraft]
  );

  const setAgentType = useCallback(
    (newAgentType: AgentType | null) => {
      if (newAgentType != null) {
        updateDraft({agentType: newAgentType});
      }
    },
    [updateDraft]
  );

  const setPublicDataEnabled = useCallback(
    (enabled: boolean) => {
      updateDraft({publicDataEnabled: enabled});
    },
    [updateDraft]
  );

  // File management - delegates to ChatActions/ChatDraftState
  const onSelectFiles = useCallback(
    (files: Array<File>) => {
      ChatActions.selectFilesForDraft(chatId, files);
    },
    [chatId]
  );

  const onRemoveFile = useCallback(
    (key: string) => {
      removeFileFromDraft(chatId, key);
    },
    [chatId]
  );

  const isPending = draftState?.status === ChatDraftStatus.PENDING;

  return {
    inputText,
    setInputText,
    agentType,
    setAgentType,
    publicDataEnabled,
    setPublicDataEnabled,
    selectedFiles,
    hasUploadingFiles,
    isPending,
    onSelectFiles,
    onRemoveFile,
  };
}

export function ChatInput({
  chatSessionId,
  inputText,
  setInputText,
  publicDataEnabled,
  setPublicDataEnabled,
  onSubmit,
  inputDisabled,
  selectedFiles,
  onSelectFiles,
  onRemoveFile,
  agentType,
  setAgentType,
  allowedChatTypes,
  hideAIWarning,
}: ChatInputProps): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const realtimeVoiceSessionControls = useRealtimeVoiceSession(chatSessionId);

  // There may be inputText on initial mount if there was draft state.
  useEffect(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const length = textarea.value.length;
      textarea.setSelectionRange(length, length);
    }
  }, []);

  const hasFiles = selectedFiles.length > 0;
  const hasUploadingFiles = selectedFiles.some((f) => f.completedUpload == null);
  const isSubmitDisabled =
    inputDisabled || (!inputText.trim() && !hasFiles) || hasUploadingFiles || agentType == null;

  const handleMessageChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setInputText(e.target.value);
    autoResize();
  };

  const autoResize = (): void => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height to scrollHeight to fit content
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Enter' && !e.shiftKey) {
        // Enter (without shift) should submit the message if possible, and never type a newline.
        e.preventDefault();
        if (!isSubmitDisabled) {
          onSubmit();
        }
      }
    },
    [isSubmitDisabled, onSubmit]
  );

  const handleRemoveFile = useCallback(
    (file: File): void => {
      if (!inputDisabled) {
        const selectedFile = selectedFiles.find((f) => f.file === file);
        if (selectedFile) {
          onRemoveFile(selectedFile.key);
        }
      }
    },
    [inputDisabled, onRemoveFile, selectedFiles]
  );

  const handleSelectFiles = useCallback(
    (filesToSelect: Array<File>): void => {
      onSelectFiles(filesToSelect);
    },
    [onSelectFiles]
  );

  const handleFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleSelectFiles(Array.from(files));
      }
      // Reset the input value so the same file can be selected again
      e.target.value = '';
    },
    [handleSelectFiles]
  );

  const handleDrop = useCallback(
    (e: DragEvent): void => {
      e.preventDefault();
      e.stopPropagation();

      if (inputDisabled) {
        return;
      }

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        handleSelectFiles(Array.from(files));
      }
    },
    [inputDisabled, handleSelectFiles]
  );

  return (
    <div
      ref={inputContainerRef}
      className={css(styles.inputContainer)}
      {...getTestProps({component: 'ChatInput', element: 'root'})}
    >
      <DropTarget
        shouldCaptureDrop={true}
        mimeTypes={['Files']}
        dropEffect="copy"
        targetDesc="chat input"
        onDrop={handleDrop}
      >
        <div
          className={css(styles.inputGroup, inputDisabled && styles.inputGroupDisabled)}
          {...getTestProps({component: 'ChatInput', element: 'fileDropZone'})}
        >
          <WorkspaceContextChip agentType={agentType} />
          <textarea
            ref={textareaRef}
            className={css(styles.input)}
            placeholder={
              // No default agent is unexpected, but it's a valid state, so we handle it gracefully.
              agentType != null ? getAgentTypePlaceholderText(agentType) : 'Pick an agent to begin.'
            }
            autoFocus={true}
            value={inputText}
            onChange={handleMessageChange}
            onKeyDown={handleKeyDown}
            disabled={inputDisabled}
          />
          {hasFiles && (
            <div className={css(styles.fileListWrapper)}>
              <FileList>
                {selectedFiles.map((selectedFile) => (
                  <FileRow
                    key={selectedFile.key}
                    file={selectedFile.file}
                    onRequestRemove={handleRemoveFile}
                    additionalContent={!selectedFile.completedUpload && <Loader size="sm" />}
                  />
                ))}
              </FileList>
            </div>
          )}
          <div className={css(styles.inputGroupButtonRow)}>
            <div className={css(styles.inputGroupIconButtons)}>
              {/* Invisible input to trigger file picker */}
              <input
                {...getTestProps({component: 'ChatInput', element: 'fileInput'})}
                type="file"
                multiple={true}
                className="hidden"
                ref={fileInputRef}
                onChange={handleFileInputChange}
              />
              {/* Invisible input to trigger camera */}
              <input
                {...getTestProps({component: 'ChatInput', element: 'cameraInput'})}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                ref={cameraInputRef}
                onChange={handleFileInputChange}
              />
              <AgentToolbar
                chatSessionId={chatSessionId}
                textareaRef={textareaRef}
                fileInputRef={fileInputRef}
                cameraInputRef={cameraInputRef}
                allowedChatTypes={allowedChatTypes}
                agentType={agentType}
                setAgentType={setAgentType}
                inputDisabled={inputDisabled}
                publicDataEnabled={publicDataEnabled}
                setPublicDataEnabled={setPublicDataEnabled}
                realtimeVoiceSessionControls={realtimeVoiceSessionControls}
              />
            </div>
            <Button
              buttonStyle={ButtonStyle.PRIMARY}
              size={ButtonSize.MINI}
              onClick={onSubmit}
              disabled={isSubmitDisabled}
              icon="arrow-up"
              iconAltText="Send"
            />
          </div>
        </div>
      </DropTarget>
      {!hideAIWarning && (
        <div className={css(styles.warningMessage)}>AI can make mistakes. Always check results.</div>
      )}
    </div>
  );
}

function AgentToolbar({
  chatSessionId,
  textareaRef,
  fileInputRef,
  cameraInputRef,
  allowedChatTypes,
  agentType,
  setAgentType,
  inputDisabled,
  publicDataEnabled,
  setPublicDataEnabled,
  realtimeVoiceSessionControls,
}: {
  chatSessionId: string | null;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  allowedChatTypes: Array<ChatType>;
  agentType: AgentType | null;
  setAgentType: (agentType: AgentType | null) => void;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (publicDataEnabled: boolean) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  cameraInputRef: React.RefObject<HTMLInputElement>;
  inputDisabled: boolean;
  realtimeVoiceSessionControls: RealtimeVoiceSessionControls;
}): JSX.Element {
  const hasAskAgentType = isAgentTypeEnabled(AgentType.ASK);
  const isOpenAIEnabled = BenchlingConfig.LLM_ENABLE_OPENAI;

  const handleAttachButtonClick = useCallback((): void => {
    if (!inputDisabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [inputDisabled, fileInputRef]);

  const handleCameraButtonClick = useCallback((): void => {
    if (!inputDisabled && cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  }, [inputDisabled, cameraInputRef]);

  return (
    <div
      className={css(styles.agentTypeButtons)}
      onMouseDown={(e) => {
        const textArea = textareaRef.current;
        if (!textArea) {
          return;
        }
        if (document.activeElement === textArea) {
          // Don't steal focus from the text area
          e.preventDefault();
        } else {
          // Focus is if it's not already.
          textArea.focus();
        }
      }}
    >
      {/* Show a spinner while settings are loading (only possible for existing chats) */}
      {agentType == null ? (
        <Spinner aria-label="Loading agent settings" />
      ) : (
        getAvailableAgentTypes(allowedChatTypes).map((option: AgentType) => {
          const isSpecializedAgent = agentType != null && agentType !== AgentType.ASK;

          // We will only render the selected agent if the selected agent is not ASK.
          if (isSpecializedAgent && option !== agentType) {
            return null;
          }
          // It we are just showing the selected agent, then provide a close icon to see all agents...
          // ... but only if there is another agent to show.
          const showCloseIcon = isSpecializedAgent && getAvailableAgentTypes(allowedChatTypes).length > 1;
          const toggleButton = (
            <ToggleButton
              key={option}
              isActive={option === agentType}
              showCloseIcon={showCloseIcon}
              onClick={() => {
                // Don't allow toggling off a selected agent that has no close icon
                if (isSpecializedAgent && !showCloseIcon) {
                  return;
                }
                setAgentType(isSpecializedAgent ? (hasAskAgentType ? AgentType.ASK : null) : option);
              }}
              icon={getAgentTypeIcon(option) ?? undefined}
              text={getAgentTypeText(option)}
              disabled={inputDisabled}
              testKey={option}
            />
          );

          if (option === AgentType.ASK) {
            return (
              <Tooltip key={option} label="Find answers fast" position="bottom">
                <span style={{display: 'inline-block'}}>{toggleButton}</span>
              </Tooltip>
            );
          }

          if (option === AgentType.DEEP_RESEARCH) {
            return (
              <Tooltip key={option} label="Answer complex questions" position="bottom">
                <span style={{display: 'inline-block'}}>{toggleButton}</span>
              </Tooltip>
            );
          }

          if (option === AgentType.DATA_CAPTURE) {
            return (
              <Tooltip key={option} label="Create lab entries" position="bottom">
                <span style={{display: 'inline-block'}}>{toggleButton}</span>
              </Tooltip>
            );
          }
          if (option === AgentType.LEGACY_DATA_IMPORT) {
            return (
              <Tooltip key={option} label="Import and transcribe data" position="bottom">
                <span style={{display: 'inline-block'}}>{toggleButton}</span>
              </Tooltip>
            );
          }
          return toggleButton;
        })
      )}
      {agentType != null && doesAgentTypeSupportPublicData(agentType) && (
        <Tooltip
          label={
            isOpenAIEnabled
              ? 'Use public data to help answer your question.'
              : 'OpenAI must be enabled to use this feature.'
          }
          position="bottom"
        >
          <span style={{display: 'inline-block'}}>
            <ToggleButton
              isActive={publicDataEnabled}
              onClick={() => {
                setPublicDataEnabled(!publicDataEnabled);
              }}
              showCloseIcon={false}
              icon="globe"
              text="Include public data"
              disabled={inputDisabled || !isOpenAIEnabled}
            />
          </span>
        </Tooltip>
      )}
      <Tooltip label="Add files" position="bottom">
        <span style={{display: 'inline-block'}}>
          <Button
            aria-label="Upload attachments"
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            disabled={inputDisabled}
            icon="paperclip"
            onClick={handleAttachButtonClick}
            size={ButtonSize.MINI}
          />
        </span>
      </Tooltip>
      {isLikelyMobileCapabilities() && (
        <Button
          aria-label="Take and upload photo"
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          disabled={inputDisabled}
          icon="snapshot"
          onClick={handleCameraButtonClick}
          size={ButtonSize.MINI}
        />
      )}
      {BenchlingConfig.LLM_ENABLE_REALTIME_VOICE_IN_COMPOSE &&
        agentType === AgentType.DATA_CAPTURE &&
        chatSessionId != null &&
        (() => {
          switch (realtimeVoiceSessionControls.sessionState) {
            case RealtimeVoiceSessionState.NOT_CONNECTED:
              return (
                <Button
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  size={ButtonSize.MINI}
                  onClick={realtimeVoiceSessionControls.startVoiceSession}
                  disabled={inputDisabled}
                  icon="sparkles"
                  text="Go live"
                />
              );
            case RealtimeVoiceSessionState.CONNECTING:
              return (
                <Button
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  size={ButtonSize.MINI}
                  disabled={true}
                  text="Connecting..."
                />
              );
            case RealtimeVoiceSessionState.CONNECTED:
              return (
                <Button
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  size={ButtonSize.MINI}
                  onClick={realtimeVoiceSessionControls.stopVoiceSession}
                  disabled={inputDisabled}
                  icon="stop"
                  text="Stop"
                />
              );
            default:
              return null;
          }
        })()}
    </div>
  );
}

/**
 * A toggle button used to pick an agent or an agent feature like public data.
 */
function ToggleButton(props: {
  isActive: boolean;
  text: React.ReactNode;
  showCloseIcon: boolean;
  onClick: () => void;
  icon: BenchlingIconName | undefined;
  disabled: boolean;
  testKey?: string;
}): JSX.Element {
  const {isActive, text, showCloseIcon, onClick, icon, disabled, testKey} = props;
  return (
    // NB: I did not see an out of the box toggle button in Mantine v6.
    <Button
      aria-pressed={isActive}
      className={classNames({'is-active': isActive}, css(styles.agentTypeButton))}
      size={ButtonSize.MINI}
      buttonStyle={isActive ? ButtonStyle.TERTIARY_BLUE : ButtonStyle.TERTIARY_WHITE}
      onClick={onClick}
      icon={icon}
      textClassName={css(styles.agentTypeText)}
      text={
        <>
          {text}
          {showCloseIcon && <BenchlingIcon className={css(styles.closeIcon)} icon="close" />}
        </>
      }
      disabled={disabled}
      dataTestAttrs={testKey ? {component: 'ChatInput', element: 'agentToggle', key: testKey} : undefined}
    />
  );
}

const styles = StyleSheet.create({
  inputContainer: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '4px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    border: `1px solid ${GRAY}`,
    borderRadius: '16px',
    backgroundColor: WHITE,
    transition: 'box-shadow 0.1s ease-in',
    ':focus-within': {
      border: `1px solid ${GRAY_DARK}`,
      boxShadow: '0px 0px 2px 0px #00000040',
    },
  },
  inputGroupDisabled: {
    backgroundColor: GRAY_LIGHTER,
  },
  input: {
    flexGrow: 1,
    width: '100%',
    minHeight: '80px',
    padding: '12px',
    fontFamily: 'inherit',
    fontSize: '16px',
    border: 'none',
    background: 'transparent',
    resize: 'none',
    overflow: 'hidden',
    ':focus': {
      outline: 'none',
    },
  },
  inputGroupButtonRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '8px 12px',
  },
  inputGroupIconButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
  },
  publicDataToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  emailToggle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '4px 0',
  },
  warningMessage: {
    fontSize: '12px',
    color: GRAY_DARKER,
    textAlign: 'center',
    marginTop: '4px',
    marginBottom: '8px',
  },
  fileListWrapper: {
    padding: '8px 12px',
    borderTop: `1px solid ${GRAY_LIGHTER}`,
    // Ugly hack: The global `table` class on FileList adds 17px margin-bottom. Offset that with a
    // negative margin so that this component isn't imposing its own margins and instead lets its
    // parent handle spacing.
    marginBottom: -17,
  },
  indicator: {
    backgroundColor: BLUE_LIGHT,
  },
  agentTypeButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  },
  agentTypeButton: {
    fontWeight: 600,
    fontSize: '12px',
    padding: '4px 6px', // reduce horizontal padding, we are in tight space.
  },
  closeIcon: {
    marginLeft: '6px',
    marginRight: 0, // button has padding.
  },
  agentTypeText: {
    flex: 'none',
  },
});
